import React, { useCallback, useContext, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import Loading from '../../../../../molecules/Loading'
import EmptySection from '../../../../../atoms/EmptySection'
import SydButton from '../../../../../commonDesign/Button'
import OperationalTable from '../../../../../organisms/OperationalTable'
import { useListListeners, usePurgeStaleListeners } from '../../../../../../api/listeners'
import { WorkflowToolbarContext } from '../WorkflowManagement'

const useStyles = makeStyles({
  stale: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: '#FFF3E0',
    color: '#E65100'
  },
  active: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 600,
    backgroundColor: '#E8F5E9',
    color: '#2E7D32'
  }
})

function EphemeralListenerList () {
  const classes = useStyles()
  const { data: listeners, isLoading } = useListListeners({ isEphemeral: true })
  const { mutateAsync: purgeStale, isLoading: isPurging } = usePurgeStaleListeners()
  const toolbarNode = useContext(WorkflowToolbarContext)

  // Frozen at mount time — staleCount won't update as listeners expire while the
  // page stays open. Acceptable for this internal admin view (4h TTL). If real-time
  // accuracy is needed, replace with an interval-based refresh or re-derive from dayjs() inline.
  const now = useMemo(() => dayjs(), [])

  const staleCount = useMemo(() => {
    if (!listeners?.length) return 0
    return listeners.filter(l => l.expireDate && dayjs(l.expireDate).isBefore(now)).length
  }, [listeners, now])

  const handlePurge = useCallback(async () => {
    await purgeStale({})
  }, [purgeStale])

  const columns = useMemo(() => [
    { id: 'daggerId', accessor: 'daggerId', Header: 'Dagger ID' },
    { id: 'eventType', accessor: 'eventType', Header: 'Event Type' },
    { id: 'dagId', accessor: 'dagId', Header: 'DAG ID' },
    { id: 'dagStepId', accessor: 'dagStepId', Header: 'Step ID' },
    { id: 'completeOn', accessor: 'completeOn', Header: 'Complete On' },
    { id: 'failOn', accessor: 'failOn', Header: 'Fail On' },
    {
      id: 'createdDate',
      accessor: 'createdDate',
      Header: 'Created',
      Cell: ({ value }) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '\u2014' // eslint-disable-line react/prop-types
    },
    {
      id: 'expireDate',
      accessor: 'expireDate',
      Header: 'Expires',
      Cell: ({ value }) => value ? dayjs(value).format('MMM D, YYYY h:mm A') : '\u2014' // eslint-disable-line react/prop-types
    },
    {
      id: 'status',
      Header: 'Status',
      /* eslint-disable react/prop-types */
      Cell: ({ row }) => {
        const expire = row.original.expireDate
        const isStale = expire && dayjs(expire).isBefore(dayjs())
        return (
          <span className={isStale ? classes.stale : classes.active}>
            {isStale ? 'Stale' : 'Active'}
          </span>
        )
      }
      /* eslint-enable react/prop-types */
    }
  ], [classes])

  if (isLoading) return <Loading />

  return (
    <>
      {toolbarNode && createPortal(
        <SydButton
          size='sm'
          variant='outline'
          icon='delete_sweep'
          onClick={handlePurge}
          disabled={staleCount === 0 || isPurging}
          processing={isPurging}
        >
          Purge Stale ({staleCount})
        </SydButton>,
        toolbarNode
      )}
      {!listeners?.length ? (
        <EmptySection
          title='No Ephemeral Listeners'
          description='Ephemeral listeners are created at runtime for async step completion and cleaned up automatically.'
        />
      ) : (
        <OperationalTable
          variant='v2'
          columns={columns}
          data={listeners}
          defaultSort={[{ id: 'expireDate', desc: false }]}
        />
      )}
    </>
  )
}

export default EphemeralListenerList
